import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { domainLabel } from "../lib/agents";
import { DOMAIN_NOTES, defaultDomains, requestProblem, safeRedirect, sortDomains } from "../lib/oauthConsent";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import logo from "../assets/jamora-logo.png";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center px-4 py-8" data-testid="oauth-consent">
      <div className="w-full max-w-xl bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-7 space-y-5">
        <div className="flex items-center gap-3">
          <img src={logo} alt="" className="h-9 w-9" />
          <div className="font-serif-display text-xl font-bold text-jamora leading-none">Jamora CRM</div>
        </div>
        {children}
      </div>
    </div>
  );
}

function Message({ title, text, testid }) {
  return (
    <Shell>
      <div className="space-y-2" data-testid={testid}>
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-600">{text}</p>
      </div>
      <Link to="/dashboard" className="inline-flex items-center justify-center rounded-md border border-slate-200 px-4 min-h-[44px] text-sm font-medium text-slate-700 hover:bg-slate-50">Retour au tableau de bord</Link>
    </Shell>
  );
}

// Page ouverte par une application (Claude, ChatGPT, Claude Code…) qui demande à lire Jamora en votre nom (Ordre 30). La personne est connectée par sa
// session habituelle ; le serveur re-vérifie tout à la décision. Lecture seule : rien de ce qui est coché ne donne un droit de plus que le compte.
export default function OAuthConsent() {
  const [params] = useSearchParams();
  const requestId = params.get("request") || "";
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading" });
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const load = useCallback(async () => {
    setState({ phase: "loading" });
    try {
      const { data } = await api.get(`/oauth/requests/${encodeURIComponent(requestId)}`);
      setChosen(defaultDomains(data.domains));
      setState({ phase: "ready", request: data });
    } catch (e) {
      setState({ phase: requestProblem(e) });
    }
  }, [requestId]);
  useEffect(() => { if (requestId) load(); else setState({ phase: "gone" }); }, [requestId, load]);

  const domains = useMemo(() => (state.request ? sortDomains(state.request.domains, domainLabel) : []), [state.request]);
  const toggle = (id) => setChosen((c) => (c.includes(id) ? c.filter((d) => d !== id) : [...c, id]));

  const decide = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/oauth/requests/${encodeURIComponent(requestId)}/decision`, { approve, domains: approve ? chosen : [] });
      const target = safeRedirect(data.redirect_to);
      if (!target) {
        toast.error("L'adresse de retour de l'application est invalide : la connexion n'a pas été finalisée.");
        navigate("/dashboard");
        return;
      }
      setLeaving(true);
      window.location.assign(target);
    } catch (e) {
      toast.error(errorMessage(e));
      if (e?.response?.status === 404) setState({ phase: "gone" });
    } finally {
      setBusy(false);
    }
  };

  if (state.phase === "loading") return <Shell><div className="flex justify-center py-10"><Loader2 className="animate-spin text-jamora" aria-label="Chargement" /></div></Shell>;
  if (state.phase === "gone") return <Message testid="consent-gone" title="Cette demande n'est plus valable" text="Elle a expiré ou a déjà été traitée. Relancez la connexion depuis l'application." />;
  if (state.phase === "forbidden") return <Message testid="consent-forbidden" title="Vous ne pouvez pas connecter d'application" text="Votre compte n'a pas le droit de connecter des agents externes. Demandez-le à un administrateur, puis relancez la connexion depuis l'application." />;
  if (state.phase === "error") {
    return (
      <Shell>
        <div className="space-y-3" data-testid="consent-error">
          <h1 className="text-lg font-semibold text-slate-900">Impossible de lire la demande</h1>
          <p className="text-sm text-slate-600">Réessayez dans quelques instants.</p>
          <Button onClick={load} className="min-h-[44px]">Réessayer</Button>
        </div>
      </Shell>
    );
  }

  const { request } = state;
  return (
    <Shell>
      <div className="space-y-1">
        <h1 className="text-lg font-semibold text-slate-900" data-testid="consent-title">Autoriser « {request.client_name} » à lire Jamora CRM ?</h1>
        <p className="text-sm text-slate-600">Demande venant de <span className="font-medium text-slate-800" data-testid="consent-host">{request.redirect_host}</span>. N'autorisez que si c'est bien vous qui venez de lancer cette connexion.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-1.5" data-testid="consent-can">
          <h2 className="font-semibold text-emerald-800 flex items-center gap-1.5"><Check size={15} aria-hidden /> Elle pourra</h2>
          <ul className="list-disc pl-4 text-slate-700 space-y-1"><li>Lire ce que votre compte peut lire, dans les domaines cochés.</li><li>Suivre vos droits d'aujourd'hui : si vous en perdez un, elle le perd aussitôt.</li></ul>
        </section>
        <section className="rounded-lg border border-red-200 bg-red-50/60 p-3 space-y-1.5" data-testid="consent-cannot">
          <h2 className="font-semibold text-red-800 flex items-center gap-1.5"><X size={15} aria-hidden /> Elle ne pourra pas</h2>
          <ul className="list-disc pl-4 text-slate-700 space-y-1"><li>Modifier, créer ni supprimer quoi que ce soit.</li><li>Toucher aux comptes, aux droits ni aux paramètres.</li></ul>
        </section>
      </div>

      <fieldset className="space-y-1" data-testid="consent-domains">
        <legend className="text-sm font-medium text-slate-800 mb-1">Domaines partagés</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
          {domains.map((d) => (
            <label key={d.id} className="flex items-start gap-2 text-sm text-slate-700 min-h-[44px] sm:min-h-[36px] py-1">
              <Checkbox className="mt-0.5" checked={chosen.includes(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`consent-domain-${d.id}`} />
              <span>
                {domainLabel(d.id)}
                {DOMAIN_NOTES[d.id] && <span className="block text-xs text-slate-500">{DOMAIN_NOTES[d.id]}</span>}
              </span>
            </label>
          ))}
        </div>
        {chosen.length === 0 && <p className="text-xs text-red-600" role="alert" data-testid="consent-none">Cochez au moins un domaine pour autoriser.</p>}
      </fieldset>

      <p className="text-xs text-slate-500 flex items-start gap-1.5"><Lock size={13} className="mt-0.5 shrink-0" aria-hidden /> La connexion dure {request.days} jours au plus. Vous pouvez la couper à tout moment dans Paramètres › Agents externes. Ce que l'application lit est envoyé à son fournisseur.</p>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <Button variant="outline" className="min-h-[44px]" disabled={busy || leaving} onClick={() => decide(false)} data-testid="consent-refuse">Refuser</Button>
        <Button className="min-h-[44px]" disabled={busy || leaving || chosen.length === 0} onClick={() => decide(true)} data-testid="consent-approve">
          {busy || leaving ? <Loader2 size={15} className="mr-2 animate-spin" aria-hidden /> : <ShieldCheck size={15} className="mr-2" aria-hidden />}
          {leaving ? "Retour à l'application…" : "Autoriser"}
        </Button>
      </div>
    </Shell>
  );
}
